<template>
  <div class="minput" :class="{ disabled: $attrs.disabled }">
    <div v-if="$slots.label || label" class="minput-label">
      <slot name="label">
        <label for="_m_input" bold>
          {{ label }} <b v-if="required" red>*</b>
        </label>
      </slot>
    </div>
    <div v-if="$slots.desc">
      <slot name="desc" />
    </div>
    <div
      :class="[
        'minput-input',
        { hasPrepend: !!$slots.prepend, focused: isFocus },
      ]"
    >
      <div v-if="$slots.prepend" class="minput-input_prepend">
        <slot name="prepend" />
      </div>
      <input
        v-if="type === 'text'"
        id="_m_input"
        ref="minput_core"
        v-model="_value"
        class="minput-input_core"
        :type="nativeType"
        :maxlength="max || Infinity"
        :autocomplete="autocomplete"
        v-bind="$attrs"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
      />
      <textarea
        v-if="type === 'textarea'"
        id="_m_input"
        ref="minput_core"
        v-model="_value"
        class="minput-input_core textarea"
        :type="nativeType"
        :maxlength="max || Infinity"
        v-bind="$attrs"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
        @keydown="onKeydown"
      />
      <!--COUNT-->
      <div v-if="$slots.count || count" class="minput-input_count">
        <slot name="count" :data="{ len: value.length, max }">
          <div class="count_core" flex-center>
            <span>{{ value.length }}</span>
            <span>/</span>
            <span v-if="max">
              {{ max }}
            </span>
          </div>
        </slot>
      </div>
      <!--After-->
      <div v-if="$slots.after || after.length" class="minput-input_after">
        <slot name="after">
          <div class="after_core" flex-center>{{ after }}</div>
        </slot>
      </div>
    </div>
    <div v-if="errMsg" class="minput-error" red>
      {{ errMsg }}
    </div>
  </div>
</template>

<script>
import { isDef } from '../../utils/base';
// TODO: 新增校验时机/方式
export default {
  props: {
    label: {
      type: String,
      default: '',
    },
    after: {
      type: String,
      default: '',
    },
    max: {
      type: Number,
      default: null,
    },
    count: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      default: '',
    },
    errMsg: {
      type: String,
      default: '',
    },
    filter: {
      type: Function,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    nativeType: {
      type: String,
      default: 'text',
    },
    autocomplete: {
      type: String,
      default: 'off',
    }
  },
  data() {
    return {
      isFocus: false,
    };
  },
  computed: {
    _value: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    el() {
      return this.$refs.minput_core;
    },
    listeners() {
      return this.$listeners;
    },
  },
  mounted() {
    if (this.type === 'textarea') this.adjustHeight();
  },
  methods: {
    onInput(e) {
      let { value } = e.target;
      if (this.filter) {
        value = this.filter(value);
        e.target.value = value;
      }
      this.$emit('input', value);
      this.adjustHeight();
    },
    onChange(e) {
      const { value } = e.target;
      this.$emit('change', e);
      this.adjustHeight();
    },
    onBlur() {
      this.isFocus = false;
      this.$emit('blur');
    },
    onFocus() {
      this.isFocus = true;
      this.$emit('focus');
    },
    onKeydown(e) {
      this.$emit('keywodn', e);
    },
    onMouseLeave(e) {
      this.emit('mouseleave', e);
    },
    onMouseEnter(e) {
      this.emit('mouseenter', e);
    },
    focus() {
      this.el?.focus();
    },
    adjustHeight() {
      if (this.type !== 'textarea') return;
      requestAnimationFrame(() => {
        const inp = this.$refs.minput_core;
        if (isDef(inp)) {
          const parentStyle = inp.parentNode.style;
          const { overflow } = inp.style;

          // reset height of textarea to a small size to detect the real height
          // but keep the total control size the same
          // Firefox rulez #14263, #14344
          this.$q?.platform?.is?.firefox !== true &&
            (inp.style.overflow = 'hidden');
          parentStyle.marginBottom = `${inp.scrollHeight - 1}px`;
          inp.style.height = '1px';

          inp.style.height = `${inp.scrollHeight}px`;
          inp.style.overflow = overflow;
          parentStyle.marginBottom = '';
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
*[red] {
  color: $negative;
}
*[flex-center] {
  display: flex;
  align-items: center;
  justify-content: center;
}
.minput {
  &.disabled {
    pointer-events: none;
  }
  &-label {
    margin-bottom: 12px;
    font-size: 12px;
  }
  &-input {
    display: flex;
    min-height: 42px;
    border-radius: 8px;
    align-items: center;
    padding-left: 12px;
    background: #fff;
    overflow: hidden;
    transition: all 0.3s;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      pointer-events: none;
      border-radius: inherit;
      border: 1px solid rgba(0, 0, 0, 0.24);
      transition: border-color 0.36s cubic-bezier(0.4, 0, 0.2, 1);
    }
    &.focused {
      &::after {
        border-width: 2px;
        border-color: $primary;
        transform: scale3d(1, 1, 1);
      }
    }
    &_prepend {
      align-self: stretch;
      display: flex;
      align-items: center;
    }
    &_core {
      flex: 1;
      outline: none;
      border: none;
      padding: 4px 8px 4px 0;
      background: transparent;
      min-height: 42px;
      display: flex;
      &.textarea {
        padding-top: 10px;
        resize: none;
      }
    }
    &.hasPrepend {
      padding-left: 0;
      .minput-input_core {
        padding-left: 12px;
      }
    }
    &_count {
      padding: 0 6px;
      font-size: 12px;
      height: 42px;
      display: flex;
      align-items: center;
      align-self: flex-end;
      .count_core {
        color: #6b7280;
      }
    }
    &_after {
      align-self: stretch;
      display: flex;
      align-items: center;
      .after_core {
        height: 100%;
        padding: 0 8px;
        background: rgb(228, 228, 228);
      }
    }
  }
  &-error {
    padding: 4px;
    font-size: 12px;
  }
}
</style>
